import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification as useNotificationContext } from '../contexts/NotificationContext';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { AdminService, AdminUser } from '../services/adminService';
import { NotificationService, Notification } from '../services/notificationService';
import { usePageTitle } from '../hooks/usePageTitle';

const AdminNotificationPage: React.FC = () => {
  usePageTitle('Admin Notifications');
  const { currentUser } = useAuth();
  const { showNotification } = useNotificationContext();
  const { isDark } = useAdminTheme();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info' as 'info' | 'success' | 'warning' | 'error',
    recipientType: 'all' as 'all' | 'specific',
    selectedUsers: [] as string[],
    expiresAt: ''
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('AdminNotificationPage: Loading data...');

      const [usersData, notificationsData] = await Promise.all([
        AdminService.getAllUsers(),
        NotificationService.getAllNotifications()
      ]);

      console.log('AdminNotificationPage: Users loaded:', usersData.length);
      console.log('AdminNotificationPage: Notifications loaded:', notificationsData.length);

      setUsers(usersData);
      setNotifications(notificationsData);
    } catch (error) {
      console.error('AdminNotificationPage: Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleUserToggle = useCallback((userId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedUsers: prev.selectedUsers.includes(userId)
        ? prev.selectedUsers.filter(id => id !== userId)
        : [...prev.selectedUsers, userId]
    }));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('AdminNotificationPage: Form submitted with data:', formData);

    if (!currentUser) {
      console.error('AdminNotificationPage: No current user');
      return;
    }

    if (!formData.title.trim() || !formData.message.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.recipientType === 'specific' && formData.selectedUsers.length === 0) {
      alert('Please select at least one user');
      return;
    }

    try {
      setSending(true);
      console.log('AdminNotificationPage: Starting notification send...');

      const senderName = currentUser.displayName || currentUser.email || 'Admin';
      const expiresAt = formData.expiresAt ? new Date(formData.expiresAt) : undefined;

      if (formData.recipientType === 'all') {
        // Send to all users
        console.log('AdminNotificationPage: Sending to all users');
        await NotificationService.sendToAllUsers(
          formData.title,
          formData.message,
          formData.type,
          currentUser.uid,
          senderName,
          expiresAt
        );
      } else {
        // Send to specific users
        console.log('AdminNotificationPage: Sending to specific users:', formData.selectedUsers);
        const promises = formData.selectedUsers.map(userId =>
          NotificationService.sendToUser(
            userId,
            formData.title,
            formData.message,
            formData.type,
            currentUser.uid,
            senderName,
            expiresAt
          )
        );

        await Promise.all(promises);
      }

      // Show success notification
      console.log('AdminNotificationPage: Notification sent successfully, showing success message');
      showNotification({
        id: 'success',
        title: 'Notification Sent',
        message: `Notification sent to ${formData.recipientType === 'all' ? 'all users' : `${formData.selectedUsers.length} user(s)`}`,
        type: 'success',
        recipientId: 'all',
        senderId: currentUser.uid,
        senderName: 'System',
        createdAt: new Date() as any,
        isRead: false
      });

      // Reset form
      setFormData({
        title: '',
        message: '',
        type: 'info',
        recipientType: 'all',
        selectedUsers: [],
        expiresAt: ''
      });

      // Reload notifications
      await loadData();

    } catch (error) {
      console.error('AdminNotificationPage: Error sending notification:', error);
      alert('Failed to send notification. Please try again.');
    } finally {
      setSending(false);
    }
  }, [currentUser, formData, showNotification, loadData]);

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success': return '✅';
      case 'warning': return '⚠️';
      case 'error': return '❌';
      default: return 'ℹ️';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'success': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      default: return 'text-blue-600';
    }
  };

  const handleDeleteNotification = useCallback(async (notificationId: string) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;
    try {
      setDeletingId(notificationId);
      await NotificationService.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
      alert('Failed to delete notification.');
    } finally {
      setDeletingId(null);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
          <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Loading notification center...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Notification Center
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Send Notification Form */}
        <div className={`rounded-lg shadow-sm border p-6 ${isDark
            ? 'bg-gray-900 border-gray-700'
            : 'bg-white border-gray-200'
          }`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'
            }`}>Send Notification</h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter notification title"
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${isDark
                    ? 'bg-gray-800 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                  }`}
                required
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>Message *</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                placeholder="Enter notification message"
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none ${isDark
                    ? 'bg-gray-800 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                  }`}
                required
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>Type</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${isDark
                    ? 'bg-gray-800 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                  }`}
              >
                <option value="info">Info</option>
                <option value="success">Success</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>Recipients</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="recipientType"
                    value="all"
                    checked={formData.recipientType === 'all'}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    All users
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="recipientType"
                    value="specific"
                    checked={formData.recipientType === 'specific'}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Specific users
                  </span>
                </label>
              </div>
            </div>

            {formData.recipientType === 'specific' && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>Select Users</label>
                <div className={`max-h-32 overflow-y-auto border rounded-md p-2 ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
                  }`}>
                  {users.map(user => (
                    <label key={user.id} className="flex items-center py-1">
                      <input
                        type="checkbox"
                        checked={formData.selectedUsers.includes(user.id)}
                        onChange={() => handleUserToggle(user.id)}
                        className="mr-2"
                      />
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {user.email}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>Expires At (Optional)</label>
              <input
                type="datetime-local"
                name="expiresAt"
                value={formData.expiresAt}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${isDark
                    ? 'bg-gray-800 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                  }`}
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              className={`w-full py-2 px-4 rounded-md font-medium transition-colors duration-200 ${sending
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-cyan-600 text-white hover:bg-cyan-700 focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2'
                }`}
            >
              {sending ? 'Sending...' : 'Send Notification'}
            </button>
          </form>
        </div>

        {/* Recent Notifications */}
        <div className={`rounded-lg shadow-sm border p-6 ${isDark
            ? 'bg-gray-900 border-gray-700'
            : 'bg-white border-gray-200'
          }`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'
            }`}>Recent Notifications</h3>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                No notifications sent yet.
              </p>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`p-3 rounded-md border ${isDark
                      ? 'bg-gray-800 border-gray-700'
                      : 'bg-gray-50 border-gray-200'
                    }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-lg">{getNotificationIcon(notification.type)}</span>
                        <h4 className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'
                          }`}>
                          {notification.title}
                        </h4>
                      </div>
                      <p className={`text-sm mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                        {notification.message}
                      </p>
                      <div className="flex items-center justify-between text-xs">
                        <span className={`${getNotificationColor(notification.type)}`}>
                          {notification.type.toUpperCase()}
                        </span>
                        <span className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {formatTimestamp(notification.createdAt)}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteNotification(notification.id)}
                      disabled={deletingId === notification.id}
                      className={`ml-2 p-1.5 rounded-md transition-colors duration-200 flex-shrink-0 ${deletingId === notification.id
                          ? 'opacity-50 cursor-not-allowed'
                          : isDark
                            ? 'text-gray-500 hover:text-red-400 hover:bg-red-500/10'
                            : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                        }`}
                      title="Delete notification"
                    >
                      {deletingId === notification.id ? (
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNotificationPage;

